/**
 * Input component with dark theme styling.
 */
import { forwardRef, InputHTMLAttributes, useId } from 'react';
import { clsx } from 'clsx';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className, id: providedId, ...props }, ref) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="mb-1.5 block text-sm font-medium text-dark-300"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          className={clsx(
            'block w-full rounded-lg border bg-dark-800 px-3 py-2 text-sm text-dark-100 transition-all',
            'placeholder:text-dark-500',
            'focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-offset-dark-900',
            error
              ? 'border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/30'
              : 'border-dark-600 focus:border-primary-500 focus:ring-primary-500/30',
            'disabled:cursor-not-allowed disabled:bg-dark-900 disabled:text-dark-500',
            className
          )}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          {...props}
        />
        {error && (
          <p id={errorId} className="mt-1.5 text-sm text-rose-400" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="mt-1.5 text-sm text-dark-500">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
